import React, { useEffect, useState } from 'react'

import Input from 'components/Form/Input'
import Select from 'components/Form/Select'
import {
  ActivityFilters,
  CustomDates,
  DateField,
  FilterControl,
  FilterGrid,
  FilterLabel,
  Filters,
  FiltersHeader,
  FiltersTitle,
  PeriodButton,
  Periods,
  ResetButton,
} from './styled'

const PERIODS = ['today', '7d', '30d', '90d', 'year', 'custom']
const PERIOD_KEYS = {
  today: 'analytics_filter_today',
  '7d': 'analytics_filter_7_days',
  '30d': 'analytics_filter_30_days',
  '90d': 'analytics_filter_90_days',
  year: 'analytics_filter_this_year',
  custom: 'analytics_filter_custom_range',
}

const withAll = (options, label) => [{ id: '', title: label }, ...(options || [])]
const AREA_OPTIONS = [
  { id: 'universe', title: 'Universe' },
  { id: 'accelerator', title: 'Accelerator' },
  { id: 'lite', title: 'Lite' },
]

export default function AnalyticsFilters({ filters = {}, filterOptions = {}, section, setFilter, resetFilters, t }) {
  const [search, setSearch] = useState(filters.search || filters.content || filters.user || filters.event || '')

  useEffect(() => {
    setSearch(filters.search || filters.content || filters.user || filters.event || '')
  }, [filters.content, filters.event, filters.search, filters.user])

  const applyTextFilter = () => setFilter('search', search.trim())

  return (
    <Filters aria-label={t('analytics_filters_active')}>
      <FiltersHeader>
        <FiltersTitle>{t('analytics_filters_active')}</FiltersTitle>
        <ResetButton onClick={resetFilters} type="button">{t('analytics_filter_reset')}</ResetButton>
      </FiltersHeader>

      <Periods aria-label={t('analytics_filter_period')}>
        {PERIODS.map(period => (
          <PeriodButton
            active={filters.period === period}
            key={period}
            onClick={() => setFilter('period', period)}
            type="button"
          >
            {t(PERIOD_KEYS[period])}
          </PeriodButton>
        ))}
      </Periods>

      {filters.period === 'custom' || section === 'surveys' ? (
        <CustomDates>
          <DateField>
            <FilterLabel htmlFor="analytics-from">{t('analytics_filter_from')}</FilterLabel>
            <Input holder id="analytics-from" max={filters.to || undefined} onChange={event => setFilter('from', event.target.value)} placeholder={t('analytics_filter_from')} type="date" value={filters.from || ''} />
          </DateField>
          <DateField>
            <FilterLabel htmlFor="analytics-to">{t('analytics_filter_to')}</FilterLabel>
            <Input holder id="analytics-to" min={filters.from || undefined} onChange={event => setFilter('to', event.target.value)} placeholder={t('analytics_filter_to')} type="date" value={filters.to || ''} />
          </DateField>
        </CustomDates>
      ) : null}

      <FilterGrid>
        {section === 'competitive' ? null : <FilterControl>
          <Select
            id="analytics-persona"
            onChange={value => setFilter('persona', value)}
            options={filterOptions.personas}
            placeholder={t('analytics_filter_persona')}
            small
            multiple value={filters.persona || ''}
          />
        </FilterControl>}
        {section === 'competitive' ? null : <FilterControl>
          <Select
            id="analytics-course"
            onChange={value => setFilter('course', value)}
            options={filterOptions.courses}
            placeholder={t('analytics_filter_course')}
            small
            multiple value={filters.course || ''}
          />
        </FilterControl>}
        {section === 'competitive' ? null : <FilterControl>
          <Select
            id="analytics-category"
            onChange={value => setFilter('category', value)}
            options={filterOptions.categories}
            placeholder={t('analytics_filter_category')}
            small
            multiple value={filters.category || ''}
          />
        </FilterControl>}
        {!filterOptions.programs?.length ? null : (
          <FilterControl>
            <Select
              id="analytics-program"
              onChange={value => setFilter('program', value)}
              options={withAll(filterOptions.programs, t('analytics_filter_all'))}
              placeholder={t('analytics_filter_program_portal')}
              small
              value={filters.program || ''}
            />
          </FilterControl>
        )}
        {section === 'certificates' && filterOptions.trainers?.length ? (
          <FilterControl>
            <Select id="analytics-trainer" multiple onChange={value => setFilter('trainer', value)} options={filterOptions.trainers} placeholder={t('analytics_filter_trainer')} small value={filters.trainer || ''} />
          </FilterControl>
        ) : null}
        {section === 'surveys' ? (
          <FilterControl>
            <Select
              id="analytics-survey"
              onChange={value => setFilter('survey', value)}
              options={withAll(filterOptions.surveys, t('analytics_filter_all_surveys'))}
              placeholder={t('analytics_filter_survey')}
              small
              value={filters.survey || ''}
            />
          </FilterControl>
        ) : null}
        <FilterControl>
          <Select id="analytics-area" onChange={value => setFilter('area', value)} options={withAll(filterOptions.areas?.length ? filterOptions.areas : AREA_OPTIONS, t('analytics_filter_all'))} placeholder={t('analytics_filter_area')} small value={filters.area || ''} />
        </FilterControl>
        {section === 'competitive' ? (
          <>
            <FilterControl><Select id="analytics-competitive-section" onChange={value => setFilter('competitiveSection', value)} options={withAll(filterOptions.competitiveSections, t('analytics_filter_all'))} placeholder={t('analytics_filter_competitive_section')} small value={filters.competitiveSection || ''} /></FilterControl>
            <FilterControl><Select id="analytics-competitor" onChange={value => setFilter('competitor', value)} options={withAll(filterOptions.competitors, t('analytics_filter_all'))} placeholder={t('analytics_filter_competitor')} small value={filters.competitor || ''} /></FilterControl>
            <FilterControl><Select id="analytics-competitive-source" onChange={value => setFilter('source', value)} options={withAll(filterOptions.competitiveSources, t('analytics_filter_all'))} placeholder={t('analytics_filter_source')} small value={filters.source || ''} /></FilterControl>
            <FilterControl><Select id="analytics-assistant" onChange={value => setFilter('assistant', value)} options={withAll(filterOptions.assistants, t('analytics_filter_all'))} placeholder={t('analytics_filter_assistant')} small value={filters.assistant || ''} /></FilterControl>
          </>
        ) : null}
      </FilterGrid>

      {['activity', 'certificates'].includes(section) ? (
        <ActivityFilters>
          <Input
            action={applyTextFilter}
            holder
            onBlur={applyTextFilter}
            onChange={event => setSearch(event.target.value)}
            onSubmitEditing={applyTextFilter}
            placeholder={t('analytics_filter_global_search')}
            type="search"
            value={search}
          />
        </ActivityFilters>
      ) : null}
    </Filters>
  )
}
